import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { resolveVideo, prefetchResolve } from '../utils/api'
import { prefetchPlayerPage } from '../utils/prefetch'
import { downloadResult, isEmbedSource, sharePending } from '../utils/download'
import { getHistory, clearHistory, type HistoryEntry } from '../utils/history'
import { APP_VERSION } from '../version'
import styles from './HomePage.module.css'

const SUPPORTED_FORMATS = [
  { icon: '🎬', label: 'MP4 / M4V', desc: 'Direct video files' },
  { icon: '📡', label: 'HLS / M3U8', desc: 'Live & VOD streams' },
  { icon: '📦', label: 'TeraBox', desc: 'Protected cloud player' },
  { icon: '💿', label: 'DiskWala', desc: 'Cloud stream player' },
  { icon: '🎞️', label: 'WebM', desc: 'Open web video format' },
  { icon: '🌐', label: 'DASH / MPD', desc: 'Adaptive streaming' },
  { icon: '☁️', label: 'Public CDN', desc: 'S3, CloudFront & more' },
  { icon: '▶️', label: 'YouTube', desc: 'Via official embed' },
  { icon: '🎥', label: 'Vimeo', desc: 'Via official embed' },
  { icon: '🎭', label: 'MOV / QuickTime', desc: 'When browser-compatible' },
]

const FEATURES = [
  { icon: '🚫', label: 'Ad-Free Interface', desc: 'No popups, no fake download buttons' },
  { icon: '⚡', label: 'Fast Playback', desc: 'Streaming, not downloading' },
  { icon: '📡', label: 'HLS Support', desc: 'Adaptive bitrate streaming' },
  { icon: '📱', label: 'Mobile Optimized', desc: 'iPhone Safari compatible' },
  { icon: '🪟', label: 'Picture-in-Picture', desc: 'Keep watching while browsing' },
  { icon: '💬', label: 'Subtitle Support', desc: 'WebVTT subtitle tracks' },
  { icon: '🎚️', label: 'Quality Selection', desc: 'Choose your stream quality' },
  { icon: '🔒', label: 'SSRF Protected', desc: 'Secure server-side validation' },
]

const EXAMPLE_URLS = [
  'https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8',
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
  'https://storage.googleapis.com/shaka-demo-assets/angel-one/dash.mpd',
]

export function HomePage() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropZoneRef = useRef<HTMLDivElement>(null)

  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [pasteAnimation, setPasteAnimation] = useState(false)
  const [dl, setDl] = useState({ busy: false, pct: 0, shareReady: false })

  useEffect(() => {
    getHistory().then(h => { setHistory(h); setHistoryLoaded(true) })
  }, [])

  // Start resolving the link AND downloading the player bundle while the user
  // is still on this page - tapping Play then needs no network at all.
  const warmPlayback = useCallback((link: string, complete = false) => {
    prefetchResolve(link, complete)
    prefetchPlayerPage()
  }, [])

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (text.startsWith('http://') || text.startsWith('https://')) {
        setUrl(text)
        // The URL is complete the moment it is pasted -> start resolving now
        // so the Play tap does not have to wait for the network.
        warmPlayback(text, true)
        setPasteAnimation(true)
        setTimeout(() => setPasteAnimation(false), 800)
      }
    } catch { /* ignore */ }
  }, [warmPlayback])

  // Same warm-up for a typed/pasted link, once the value looks complete.
  // Half-typed input never reaches the edge.
  useEffect(() => {
    const trimmed = url.trim()
    if (!trimmed) return
    const t = setTimeout(() => warmPlayback(trimmed), 700)
    return () => clearTimeout(t)
  }, [url, warmPlayback])

  const handlePlay = useCallback(async () => {
    const trimmed = url.trim()
    if (!trimmed) { setError('Please enter a video URL.'); return }
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      setError('URL must start with http:// or https://')
      return
    }
    setError('')
    setLoading(true)
    try {
      const result = await resolveVideo(trimmed)
      if (result.success) {
        navigate('/player', { state: { result, url: trimmed } })
      } else {
        setError(result.error)
        setLoading(false)
      }
    } catch {
      setError('Network error. Please check your connection.')
      setLoading(false)
    }
  }, [url, navigate])

  const handleDownload = useCallback(async () => {
    const trimmed = url.trim()
    if (!trimmed) { setError('Please enter a video URL.'); return }
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      setError('URL must start with http:// or https://')
      return
    }
    setError('')
    setDl({ busy: true, pct: 0, shareReady: false })
    let needsShare = false
    try {
      let result = await resolveVideo(trimmed)
      // TeraBox native resolve fails transiently sometimes -> retry once so
      // terabox links download instead of getting stuck on the embed player.
      const isTeraEmbed = result.success && isEmbedSource(result) &&
        ((result.provider || '') === 'terabox' || (result.source || '').includes('terabox'))
      if (isTeraEmbed) result = await resolveVideo(trimmed)
      if (!result.success) { setError(result.error); return }
      const res = await downloadResult(result, (pct) => setDl({ busy: true, pct, shareReady: false }))
      if (res.needsShare) {
        // iOS: try the share sheet right away; else keep the tap-to-save button.
        const s = await sharePending()
        if (s.ok || s.message) {
          if (s.message) setError(s.message)
        } else {
          needsShare = true
          setError('Share sheet-e "Save Video" tap korle Photos app-e save hobe.')
        }
      } else if (!res.ok) {
        setError(res.message || 'Download possible na.')
      }
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setDl({ busy: false, pct: 0, shareReady: needsShare })
    }
  }, [url])

  // iOS: tap opens the share sheet -> "Save Video" writes to Photos.
  const handleShare = useCallback(async () => {
    const r = await sharePending(true)
    if (r.cancelled) return // sheet dismissed - file stays ready
    setDl({ busy: false, pct: 0, shareReady: false })
    if (r.message) setError(r.message)
    else if (r.ok) setError('✅ Saved! Photos app-e check koro.')
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePlay()
  }, [handlePlay])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const text = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text/uri-list')
    if (text) { setUrl(text.trim()); setError(''); warmPlayback(text.trim(), true) }
  }, [warmPlayback])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleClearHistory = useCallback(async () => {
    await clearHistory()
    setHistory([])
  }, [])

  const handleHistoryPlay = useCallback(async (entry: HistoryEntry) => {
    setLoading(true)
    setError('')
    const result = await resolveVideo(entry.url)
    if (result.success) {
      navigate('/player', { state: { result, url: entry.url, startPosition: entry.lastPosition, historyId: entry.id } })
    } else {
      setError(result.error)
      setLoading(false)
    }
  }, [navigate])

  return (
    <div className={styles.page}>
      {/* Background Effects */}
      <div className={styles.bgGradient} aria-hidden="true" />
      <div className={styles.bgGrid} aria-hidden="true" />
      <div className={styles.bgOrb1} aria-hidden="true" />
      <div className={styles.bgOrb2} aria-hidden="true" />

      {/* Hero Section */}
      <section className={styles.hero} aria-label="Hero">
        <div className={styles.container}>
          <div className={styles.heroContent}>
            <div className={styles.badge}>
              <span className={styles.badgeDot} />
              <span>v{APP_VERSION} • Connected to GitHub • 24/7 Cloud Edge</span>
            </div>

            <h1 className={styles.heroTitle}>
              Play Your <span className={styles.gradientText}>Video</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Paste a supported video link and watch it in our clean, ad-free player.
            </p>
            <p className={styles.heroTagline}>Paste. Play. Enjoy.</p>

            {/* URL Input Area */}
            <div
              ref={dropZoneRef}
              className={`${styles.inputCard} ${dragOver ? styles.dragOver : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={() => setDragOver(false)}
            >
              <div className={styles.dragHint}>
                {dragOver ? (
                  <span className={styles.dragActive}>Drop URL here</span>
                ) : (
                  <span className={styles.dragText}>Drop a URL here or paste below</span>
                )}
              </div>

              <div className={styles.inputRow}>
                <input
                  ref={inputRef}
                  id="video-url-input"
                  type="url"
                  className={`${styles.urlInput} ${pasteAnimation ? styles.pasteFlash : ''}`}
                  placeholder="Paste video link here… (MP4, M3U8, WebM, DASH)"
                  value={url}
                  onChange={e => { setUrl(e.target.value); setError('') }}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  spellCheck="false"
                  aria-label="Video URL input"
                />
                <button
                  id="btn-paste"
                  className={styles.pasteBtn}
                  onClick={handlePaste}
                  title="Paste from clipboard"
                  aria-label="Paste from clipboard"
                >
                  📋
                </button>
              </div>

              {error && (
                <div className={styles.errorMsg} role="alert" aria-live="polite">
                  <div className={styles.errorHeader}>
                    <span className={styles.errorIcon}>⚠️</span>
                    <div>{error}</div>
                  </div>
                  {(url.toLowerCase().includes('terabox') ||
                    url.toLowerCase().includes('terafile') ||
                    url.toLowerCase().includes('jotted') ||
                    url.toLowerCase().includes('dubox') ||
                    url.toLowerCase().includes('tibibox') ||
                    url.toLowerCase().includes('diskwala')) && (
                    <div className={styles.errorHelp}>
                      <div className={styles.errorHelpTitle}>
                        💡 এই ভিডিওটি Akash-এ চালানোর নিয়ম:
                      </div>
                      <ol className={styles.errorHelpList}>
                        <li>
                          TeraBox বা DiskWala লিংকটি যেকোনো ফ্রি <strong>Direct Link Bot</strong> বা ডাউনলোডার টুলে দিয়ে সরাসরি <strong>.mp4</strong> লিংক বের করুন।
                        </li>
                        <li>
                          সেই ডিরেক্ট <strong>.mp4</strong> লিংকটি এখানে পেস্ট করে <strong>Play Video</strong> চাপুন। এটি সম্পূর্ণ বিজ্ঞাপন ছাড়া ক্লিয়ার প্লেয়ারেই চলবে!
                        </li>
                      </ol>
                    </div>
                  )}
                </div>
              )}

              <div className={styles.actionRow}>
                <button
                  id="btn-play"
                  className={`${styles.playBtn} ${loading ? styles.loading : ''}`}
                  onClick={handlePlay}
                  disabled={loading || !url.trim()}
                  aria-label="Play video"
                >
                  {loading ? (
                    <>
                      <span className={styles.btnSpinner} />
                      Resolving…
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
                      Play Video
                    </>
                  )}
                </button>
                <button
                  id="btn-download-home"
                  className={styles.downloadBtn}
                  onClick={dl.shareReady ? handleShare : handleDownload}
                  disabled={loading || dl.busy || !url.trim()}
                  aria-label="Download video"
                >
                  {dl.busy ? (
                    <>
                      <span className={styles.btnSpinner} />
                      {dl.pct > 0 ? `⬇ ${dl.pct}%` : 'Preparing…'}
                    </>
                  ) : dl.shareReady ? (
                    <>💾 Save to Photos</>
                  ) : (
                    <>⬇ Download</>
                  )}
                </button>
                <button
                  id="btn-clear"
                  className={styles.clearBtn}
                  onClick={() => { setUrl(''); setError(''); inputRef.current?.focus() }}
                  disabled={!url}
                  aria-label="Clear URL"
                >
                  Clear
                </button>
              </div>

              <div className={styles.examples}>
                <span className={styles.examplesLabel}>Try an example:</span>
                {EXAMPLE_URLS.map((u, i) => (
                  <button
                    key={i}
                    id={`example-${i}`}
                    className={styles.exampleBtn}
                    onClick={() => { setUrl(u); setError(''); warmPlayback(u, true) }}
                  >
                    {i === 0 ? 'HLS Stream' : i === 1 ? 'MP4 Video' : 'DASH Stream'}
                  </button>
                ))}
              </div>
            </div>

            {/* Route visitors to the install / download page */}
            <Link to="/download" className={styles.appCta} id="cta-download">
              <span className={styles.appCtaIcon}>📱</span>
              <span className={styles.appCtaText}>
                <strong>App হিসেবে ইনস্টল করুন</strong>
                <small>Android ও iOS • ফ্রি • অটো-আপডেট</small>
              </span>
              <span className={styles.appCtaArrow}>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Continue Watching */}
      {historyLoaded && history.length > 0 && (
        <section className={styles.section} aria-label="Continue watching">
          <div className={styles.container}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>▶</span>
                Continue Watching
              </h2>
              <button
                id="btn-clear-history"
                className={styles.clearHistoryBtn}
                onClick={handleClearHistory}
              >
                Clear History
              </button>
            </div>
            <div className={styles.historyGrid}>
              {history.slice(0, 6).map(entry => (
                <div key={entry.id} className={styles.historyCard} onClick={() => handleHistoryPlay(entry)}>
                  <div className={styles.historyThumb}>
                    {entry.thumbnail
                      ? <img src={entry.thumbnail} alt={entry.title} loading="lazy" />
                      : <div className={styles.historyThumbPlaceholder}><span>🎬</span></div>
                    }
                    {entry.duration && entry.lastPosition > 5 && (
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${Math.min(100, (entry.lastPosition / entry.duration) * 100)}%` }}
                        />
                      </div>
                    )}
                    <div className={styles.historyPlay}>▶</div>
                  </div>
                  <div className={styles.historyInfo}>
                    <p className={styles.historyTitle}>{entry.title || 'Video'}</p>
                    <p className={styles.historyMeta}>
                      {entry.provider && <span className={styles.historyProvider}>{entry.provider}</span>}
                      <span>{new Date(entry.visitedAt).toLocaleDateString()}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Supported Formats */}
      <section className={styles.section} aria-label="Supported formats">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>📂</span>
              Supported Sources
            </h2>
          </div>
          <p className={styles.sectionDesc}>
            Only publicly accessible, legally supported video sources are resolved.
            We do not bypass DRM, authentication, or access controls.
          </p>
          <div className={styles.formatsGrid}>
            {SUPPORTED_FORMATS.map(f => (
              <div key={f.label} className={styles.formatCard}>
                <span className={styles.formatIcon}>{f.icon}</span>
                <strong className={styles.formatLabel}>{f.label}</strong>
                <span className={styles.formatDesc}>{f.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={styles.section} aria-label="Features">
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>✨</span>
              Features
            </h2>
          </div>
          <div className={styles.featuresGrid}>
            {FEATURES.map(f => (
              <div key={f.label} className={styles.featureCard}>
                <span className={styles.featureIcon}>{f.icon}</span>
                <div>
                  <strong className={styles.featureLabel}>{f.label}</strong>
                  <p className={styles.featureDesc}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
